import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Modal from "@mui/material/Modal";
import React, { useState, useRef, useEffect } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CropIcon from "@mui/icons-material/Crop";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import CircularProgress, {
	CircularProgressProps,
  } from '@mui/material/CircularProgress';

import ReactCrop, {
	centerCrop,
	makeAspectCrop,
} from "react-image-crop";
import { canvasPreview } from "./canvasPreview";

import "react-image-crop/dist/ReactCrop.css";
import CircularWithValueLabel from "customComponents/Progress/CircularProgress";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 400,
	bgcolor: "background.paper",
	border: "2px solid #000",
	boxShadow: 24,
	p: 4,
};

export default function CropImageModal(props) {
	const [imgSrc, setImgSrc] = useState("");
	const [selectedFile, setSelectedFile] = useState(null);
	const [progress, setProgress] = useState(0)
	const previewCanvasRef = useRef(null);
	const fitCanvasRef = useRef(null);
	const imgRef = useRef(null);

	const [crop, setCrop] = useState();
	const [completedCrop, setCompletedCrop] = useState();
	const [scale, setScale] = useState(1);
	const [rotate, setRotate] = useState(0);
	const [aspect, setAspect] = useState(props.initialAspect ?? 1);
	const [fitMode, setFitMode] = useState(false);

	useEffect(() => {
		setProgress(0);
		setSelectedFile(null);
		setImgSrc("");
		setCompletedCrop(undefined);
		setFitMode(false);
	}, [props.open])

	function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
		return centerCrop(
			makeAspectCrop(
				{
					unit: "%",
					width: 90,
				},
				aspect,
				mediaWidth,
				mediaHeight,
			),
			mediaWidth,
			mediaHeight,
		);
	}

	function useDebounceEffect(fn, waitTime, deps) {
		useEffect(() => {
			const t = setTimeout(() => {
				fn.apply(undefined, deps);
			}, waitTime);

			return () => {
				clearTimeout(t);
			};
		}, deps);
	}

	function onSelectFile(e) {
		if (e.target.files && e.target.files.length > 0) {
			setCrop(undefined);
			const file = e.target.files[0];
			setSelectedFile(file);
			const reader = new FileReader();
			reader.addEventListener("load", () =>
				setImgSrc(reader.result?.toString() || ""),
			);
			reader.readAsDataURL(file);
		}
	}

	function onImageLoad(e) {
		if (aspect) {
			const { width, height } = e.currentTarget;
			setCrop(centerAspectCrop(width, height, aspect));
		}
	}

	async function resizeImage(blob, maxSize) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (event) => {
				const img = new Image();
				img.onload = () => {
					const canvas = document.createElement("canvas");
					const ctx = canvas.getContext("2d");
					let { width, height } = img;
					const resize = () => {
						const ratio = Math.sqrt(maxSize / blob.size);
						width *= ratio;
						height *= ratio;
						canvas.width = width;
						canvas.height = height;
						ctx.drawImage(img, 0, 0, width, height);
						canvas.toBlob((resizedBlob) => {
							// Calculate progress
							const resizedRatio = resizedBlob.size / blob.size;
							const progressValue = Math.min(
								100,
								Math.max(1, Math.round(resizedRatio * 100)),
							);
							setProgress(progressValue);
							if (resizedBlob.size <= maxSize || width <= 1 || height <= 1) {
								setProgress(100); // Final update to 100 when resizing is done
								resolve(resizedBlob);
							} else {
								// Further reduce size if needed
								const newBlob = new Blob([resizedBlob], { type: blob.type });
								blob = newBlob;
								resize();
							}
						}, blob.type);
					};
					resize();
				};
				img.src = event.target.result;
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}

	async function handleUpload() {
		const image = imgRef.current;
		if (!image || !selectedFile) {
			throw new Error("No image or file selected");
		}

		let offscreen;

		if (fitMode) {
			const canvas = fitCanvasRef.current;
			if (!canvas) throw new Error("Fit canvas does not exist");
			offscreen = new OffscreenCanvas(canvas.width, canvas.height);
			const ctx = offscreen.getContext("2d");
			ctx.drawImage(canvas, 0, 0);
		} else {
			const previewCanvas = previewCanvasRef.current;
			if (!previewCanvas || !completedCrop) {
				throw new Error("Crop canvas does not exist");
			}
			const scaleX = image.naturalWidth / image.width;
			const scaleY = image.naturalHeight / image.height;

			offscreen = new OffscreenCanvas(
				completedCrop.width * scaleX,
				completedCrop.height * scaleY,
			);
			const ctx = offscreen.getContext("2d");
			if (!ctx) throw new Error("No 2d context");
			ctx.drawImage(
				previewCanvas,
				0, 0, previewCanvas.width, previewCanvas.height,
				0, 0, offscreen.width, offscreen.height,
			);
		}

		let blob = await offscreen.convertToBlob({
			type: "image/png",
		});

		if (blob.size > props.maxSize) {
			blob = await resizeImage(blob, props.maxSize);
		}

		blob.name = selectedFile.name
		props.handleSelectImage(blob);
	}

	function renderFitPreview() {
		const image = imgRef.current;
		const canvas = fitCanvasRef.current;
		if (!image || !canvas || !aspect || !image.naturalWidth) return;

		const natW = image.naturalWidth;
		const natH = image.naturalHeight;
		const padding = 0.05;

		let canvasW, canvasH;
		if (natW / natH > aspect) {
			canvasW = natW;
			canvasH = Math.round(natW / aspect);
		} else {
			canvasH = natH;
			canvasW = Math.round(natH * aspect);
		}

		const padX = Math.round(canvasW * padding);
		const padY = Math.round(canvasH * padding);
		canvasW += padX * 2;
		canvasH += padY * 2;

		canvas.width = canvasW;
		canvas.height = canvasH;
		const ctx = canvas.getContext("2d");
		ctx.fillStyle = "#ffffff";
		ctx.fillRect(0, 0, canvasW, canvasH);

		const drawW = canvasW - padX * 2;
		const drawH = canvasH - padY * 2;
		const imgAspect = natW / natH;
		let fitW, fitH;
		if (imgAspect > drawW / drawH) {
			fitW = drawW;
			fitH = Math.round(drawW / imgAspect);
		} else {
			fitH = drawH;
			fitW = Math.round(drawH * imgAspect);
		}

		const offsetX = padX + Math.round((drawW - fitW) / 2);
		const offsetY = padY + Math.round((drawH - fitH) / 2);
		ctx.drawImage(image, offsetX, offsetY, fitW, fitH);
	}

	useEffect(() => {
		if (fitMode && imgRef.current) {
			renderFitPreview();
		}
	}, [fitMode, imgSrc]);

	useDebounceEffect(
		async () => {
			if (
				!fitMode &&
				completedCrop?.width &&
				completedCrop?.height &&
				imgRef.current &&
				previewCanvasRef.current
			) {
				canvasPreview(
					imgRef.current,
					previewCanvasRef.current,
					completedCrop,
					scale,
					rotate,
				);
			}
		},
		100,
		[completedCrop, scale, rotate],
	);

	return (
		<>
			<Modal
				open={props.open}
				onClose={props.handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box sx={style}>
					<div className="App">
						<h6>Bild auswählen:</h6>
						<div className="Crop-Controls">
							<input type="file" accept="image/*" onChange={onSelectFile} />
						</div>
						{!!imgSrc && (
							<>
								{aspect && props.rectangular && (
									<ButtonGroup
										size="small"
										fullWidth
										sx={{ mb: 1, mt: 1 }}
									>
										<Button
											variant={fitMode ? "outlined" : "contained"}
											startIcon={<CropIcon />}
											onClick={() => setFitMode(false)}
										>
											Zuschneiden
										</Button>
										<Button
											variant={fitMode ? "contained" : "outlined"}
											startIcon={<FitScreenIcon />}
											onClick={() => setFitMode(true)}
										>
											Einpassen
										</Button>
									</ButtonGroup>
								)}
								{!fitMode && (
									<>
										<h6>Bild zuschneiden:</h6>
										<ReactCrop
											circularCrop={!props.rectangular}
											crop={crop}
											onChange={(_, percentCrop) => setCrop(percentCrop)}
											onComplete={(c) => setCompletedCrop(c)}
											aspect={aspect}
											minHeight={100}
										>
											<img
												ref={imgRef}
												alt="Crop me"
												src={imgSrc}
												style={{
													transform: `scale(${scale}) rotate(${rotate}deg)`,
												}}
												onLoad={onImageLoad}
											/>
										</ReactCrop>
									</>
								)}
								{fitMode && (
									<>
										<h6>Bild einpassen:</h6>
										<img
											ref={imgRef}
											alt="Originalbild"
											src={imgSrc}
											style={{ display: "none" }}
											onLoad={() => renderFitPreview()}
										/>
									</>
								)}
							</>
						)}
						{((!fitMode && !!completedCrop) || fitMode) && !!imgSrc && (
							<>
								<div
									style={{
										display: "flex",
										justifyContent: "center",
										marginTop: 10,
										flexDirection: "column",
										alignItems: "center",
									}}
								>
									<h6 style={{ alignSelf: "flex-start" }}>Vorschau:</h6>
									<canvas
										ref={fitMode ? fitCanvasRef : previewCanvasRef}
										style={{
											border: "1px solid rgba(0,0,0,0.7)",
											borderRadius: `${props.rectangular ? "0%" : "50%"}`,
											objectFit: "contain",
											width: 300,
											height: Math.round(300 / (aspect || 1)),
										}}
									/>
								</div>
								<div style={{display: "flex", justifyContent: "center", width: "100%", marginTop: 20}}>
									{progress ? (
										<CircularWithValueLabel value={progress} />
									) : (
										<Button
											component="label"
											role={undefined}
											variant="contained"
											tabIndex={-1}
											startIcon={<CloudUploadIcon />}
											onClick={handleUpload}
										>
											Hochladen
										</Button>
									)}
								</div>
							</>
						)}
					</div>
				</Box>
			</Modal>
		</>
	);
}
